/**
 * Version Service
 * Handles fetching and parsing version data from YAML files and manifest
 */

import * as yaml from 'js-yaml';
import { compareVersions } from 'compare-versions';
import type { VersionManifest, VersionData } from '../types/version';

/**
 * Custom error class for version service failures
 */
export class VersionServiceError extends Error {
  constructor(message: string, public code: string) {
    super(message);
    this.name = 'VersionServiceError';
  }
}

/**
 * Fetch the version manifest from the public directory
 * @returns Promise with VersionManifest containing list of available versions
 * @throws VersionServiceError if manifest fetch fails
 */
async function fetchManifest(): Promise<VersionManifest> {
  try {
    const response = await fetch('/versions/manifest.json');
    if (!response.ok) {
      throw new VersionServiceError(
        `Failed to fetch manifest (HTTP ${response.status})`,
        'MANIFEST_ERROR'
      );
    }
    return response.json();
  } catch (error) {
    if (error instanceof VersionServiceError) {
      throw error;
    }
    throw new VersionServiceError(
      'Failed to fetch manifest',
      'MANIFEST_ERROR'
    );
  }
}

/**
 * Fetch and parse a single version's YAML file
 * @param version - Version string (e.g., "1.2.0")
 * @returns Promise with VersionData
 * @throws VersionServiceError if version file fetch or parsing fails
 */
async function fetchVersion(version: string): Promise<VersionData> {
  try {
    const response = await fetch(`/versions/${version}.yaml`);
    if (!response.ok) {
      throw new VersionServiceError(
        `Failed to fetch version ${version} (HTTP ${response.status})`,
        'VERSION_ERROR'
      );
    }

    const yamlText = await response.text();
    const data = yaml.load(yamlText) as VersionData;

    // Validate required fields
    if (!data || !data.version || !data.date || !data.title || !data.content) {
      throw new VersionServiceError(
        `Invalid version data for ${version}: missing required fields`,
        'INVALID_VERSION_DATA'
      );
    }

    return data;
  } catch (error) {
    if (error instanceof VersionServiceError) {
      throw error;
    }
    throw new VersionServiceError(
      `Failed to fetch version ${version}`,
      'VERSION_ERROR'
    );
  }
}

/**
 * Fetch multiple versions in parallel
 * @param versions - Array of version strings
 * @returns Promise with array of VersionData in the same order
 * @throws VersionServiceError if any fetch fails
 */
async function fetchVersions(versions: string[]): Promise<VersionData[]> {
  if (versions.length === 0) {
    return [];
  }

  try {
    return await Promise.all(versions.map(v => fetchVersion(v)));
  } catch (error) {
    if (error instanceof VersionServiceError) {
      throw error;
    }
    throw new VersionServiceError(
      'Failed to fetch multiple versions',
      'FETCH_VERSIONS_ERROR'
    );
  }
}

/**
 * Sort versions in descending order using proper semver comparison
 * @param versions - Array of version strings
 * @returns Array of versions sorted newest to oldest
 */
function sortVersionsDescending(versions: string[]): string[] {
  return versions.sort((a, b) => compareVersions(b, a));
}

/**
 * Get unread versions by comparing against the last read version
 * @param allVersions - Array of all available versions
 * @param lastReadVersion - The version the user last read, or null for first-time users
 * @returns Array of unread versions sorted newest to oldest
 */
function getUnreadVersions(
  allVersions: string[],
  lastReadVersion: string | null
): string[] {
  // First-time user - all versions are unread
  if (!lastReadVersion) {
    return sortVersionsDescending(allVersions);
  }

  // Filter versions newer than lastReadVersion using semver comparison
  const unread = allVersions.filter(
    v => compareVersions(v, lastReadVersion) > 0
  );

  return sortVersionsDescending(unread);
}

/**
 * Version Service - Main API for version operations
 */
export const versionService = {
  fetchManifest,
  fetchVersion,
  fetchVersions,
  getUnreadVersions,
  // Re-export sorting utility for use in other modules
  sortVersionsDescending,
};

export default versionService;
